import axios from "axios";
import { useState, useContext, useEffect } from "react";
import { AuthContext } from "../../context/AuthContext";
import { Button, Table, Typography, Row, Col, Select } from "antd";
import OverallReviewModal from "./OverallReviewModal";

const { Title } = Typography;
const { Option } = Select;

export default function NoticeEmployees() {
    const [employees, setEmployees] = useState([]);
    const [filteredEmployees, setFilteredEmployees] = useState([]);

    const { backUrl, authData } = useContext(AuthContext);

    const [selectedEmployee, setSelectedEmployee] = useState(null);
    const [showModal, setShowModal] = useState(false);

    const [Organisations, setOrganisations] = useState([]);
    const [departments, setDepartments] = useState([]);
    const [designations, setDesignations] = useState([]);

    const [searchEmpCode, setSearchEmpCode] = useState("");
    const [searchName, setSearchName] = useState("");

    const [organisationFilter, setOrganisationFilter] = useState("");
    const [departmentFilter, setDepartmentFilter] = useState("");
    const [designationFilter, setDesignationFilter] = useState("");



    // FETCH ORGANISATIONS
    const fetchOrganisation = async () => {
        try {
            const res = await axios.get(`${backUrl}/api/emp/organisations/`, {
                headers: { Authorization: `Bearer ${authData?.access}` },
            });
            setOrganisations(res.data.results || []);
        } catch (err) {
            console.log("Error fetching organisations", err);
        }
    };

    // FETCH DEPARTMENTS BASED ON ORG ID
    const fetchDepartments = async (orgId) => {
        try {
            const res = await axios.get(
                `${backUrl}/api/emp/departments/?organisation=${orgId}`,
                {
                    headers: { Authorization: `Bearer ${authData?.access}` },
                }
            );
            setDepartments(res.data.results || []);
        } catch (err) {
            console.log("Error fetching departments", err);
        }
    };

    // FETCH DESIGNATIONS BASED ON DEPT ID
    const fetchDesignations = async (deptId) => {
        try {
            const res = await axios.get(
                `${backUrl}/api/emp/designations/?department=${deptId}`,
                {
                    headers: { Authorization: `Bearer ${authData?.access}` },
                }
            );
            setDesignations(res.data.results || []);
        } catch (err) {
            console.log("Error fetching designations", err);
        }
    };

    useEffect(() => {
        fetchOrganisation();
    }, []);

    const fetchData = async () => {
        try {
            const res = await axios.get(
                `${backUrl}/api/probation/probation-reviews/notice-reports/?is_employee_view=true`,
                {
                    headers: {
                        Authorization: `Bearer ${authData?.access}`,
                    },
                }
            );
            setEmployees(res.data.results || []);
            setFilteredEmployees(res.data.results || []); // initial load
        } catch (error) {
            console.error("Error fetching probation reviews:", error);
        }
    };

    useEffect(() => {
        fetchData();
    }, []);

    // FILTERING LOGIC
    useEffect(() => {
        let filtered = [...employees];

        if (organisationFilter) {
            filtered = filtered.filter(
                (item) => item.organisation_name === organisationFilter
            );
        }

        if (departmentFilter) {
            filtered = filtered.filter(
                (item) => item.department_name === departmentFilter
            );
        }

        if (designationFilter) {
            filtered = filtered.filter(
                (item) => item.designation_name === designationFilter
            );
        }

        if (searchEmpCode) {
            filtered = filtered.filter((item) =>
                item.user?.username
                    ?.toLowerCase()
                    .includes(searchEmpCode.toLowerCase())
            );
        }

        if (searchName) {
            filtered = filtered.filter((item) => {
                const fullName = `${item.user?.first_name} ${item.user?.last_name}`.toLowerCase();
                return fullName.includes(searchName.toLowerCase());
            });
        }

        setFilteredEmployees(filtered);
    }, [
        organisationFilter,
        departmentFilter,
        designationFilter,
        searchEmpCode,
        searchName,
        employees,
    ]);

    const columns = [
        {
            title: "Emp Code",
            dataIndex: ["user", "username"],
            key: "emp_code",
            render: (text, record) => (
                <a
                    onClick={() => {
                        setSelectedEmployee(record);
                        setShowModal(true);
                    }}
                    style={{ cursor: "pointer" }}
                >
                    {text}
                </a>
            ),
        },
        {
            title: "Employee Name",
            render: (_, record) =>
                `${record.user?.first_name} ${record.user?.last_name}`,
        },
        {
            title: "Date of Joining",
            dataIndex: "date_of_joining",
            render: (text) => (text ? new Date(text).toLocaleDateString("en-GB") : "—"),
        },
        {
            title: "Employment Confirmed Date",
            dataIndex: "probation_end_date",
            render: (text) => (text ? new Date(text).toLocaleDateString("en-GB") : "—"),
        },
        {
            title: "Organisation",
            dataIndex: "organisation_name",
        },
        {
            title: "Department",
            dataIndex: "department_name",
        },
        {
            title: "Designation",
            dataIndex: "designation_name",
        },
    ];

    return (
        <>
            <center>
                <Title level={4}>Team Members</Title>
            </center>

            {/* FILTERS */}
            <Row gutter={[10, 10]} style={{ marginBottom: 15 }}>
                {/* Employee Code Search */}
                <Col xs={24} sm={12} md={6} lg={4}>
                    <input
                        type="text"
                        placeholder="Search Emp Code"
                        value={searchEmpCode}
                        onChange={(e) => setSearchEmpCode(e.target.value)}
                        className="ant-input"
                        style={{ width: "100%" }}
                    />
                </Col>

                {/* Name Search */}
                <Col xs={24} sm={12} md={6} lg={4}>
                    <input
                        type="text"
                        placeholder="Search Name"
                        value={searchName}
                        onChange={(e) => setSearchName(e.target.value)}
                        className="ant-input"
                        style={{ width: "100%" }}
                    />
                </Col>

                {/* Organisation */}
                <Col xs={24} sm={12} md={6} lg={4}>
                    <Select
                        placeholder="Select Organisation"
                        style={{ width: "100%" }}
                        value={organisationFilter || undefined}
                        allowClear
                        onChange={(value) => {
                            setOrganisationFilter(value);
                            setDepartmentFilter("");
                            setDesignationFilter("");
                            setDepartments([]);
                            setDesignations([]);

                            if (value) {
                                const orgObj = Organisations.find((o) => o.name === value);
                                if (orgObj) fetchDepartments(orgObj.id);
                            }
                        }}
                    >
                        {Organisations.map((org) => (
                            <Option key={org.id} value={org.name}>
                                {org.name}
                            </Option>
                        ))}
                    </Select>
                </Col>

                {/* Department */}
                <Col xs={24} sm={12} md={6} lg={4}>
                    <Select
                        showSearch
                        placeholder="Select Department"
                        style={{ width: "100%" }}
                        value={departmentFilter || undefined}
                        allowClear
                        disabled={!organisationFilter}
                        onChange={(value) => {
                            setDepartmentFilter(value);
                            setDesignationFilter("");
                            setDesignations([]);

                            const deptObj = departments.find((d) => d.name === value);
                            if (deptObj) fetchDesignations(deptObj.id);
                        }}
                    >
                        {departments.map((dept) => (
                            <Option key={dept.id} value={dept.name}>
                                {dept.name}
                            </Option>
                        ))}
                    </Select>
                </Col>

                {/* Designation */}
                <Col xs={24} sm={12} md={6} lg={4}>
                    <Select
                        showSearch
                        placeholder="Select Designation"
                        style={{ width: "100%" }}
                        value={designationFilter || undefined}
                        allowClear
                        disabled={!departmentFilter}
                        onChange={(value) => setDesignationFilter(value)}
                    >
                        {designations.map((des) => (
                            <Option key={des.id} value={des.name}>
                                {des.name}
                            </Option>
                        ))}
                    </Select>
                </Col>
            </Row>

            {/* TABLE */}
            <Table
                columns={columns}
                dataSource={filteredEmployees}
                rowKey={(record) => record.id || record.user?.id}
                pagination={{ pageSize: 50 }}
                bordered
                components={{
                    header: {
                        cell: (props) => (
                            <th
                                {...props}
                                style={{
                                    backgroundColor: "#0e497b",
                                    color: "white",
                                }}
                            >
                                {props.children}
                            </th>
                        ),
                    },
                }}
            />

            {showModal && (
                <OverallReviewModal
                    visible={showModal}
                    employee={selectedEmployee}
                    onClose={() => {
                        setShowModal(false);
                        fetchData();
                    }}
                />
            )}
        </>
    );
}
